import { Stack } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useFonts } from 'expo-font';
import { Lora_500Medium } from '@expo-google-fonts/lora';
import { Caveat_500Medium } from '@expo-google-fonts/caveat';
import { Inter_400Regular, Inter_500Medium, Inter_600SemiBold } from '@expo-google-fonts/inter';
import { colors, t } from '@hogar/shared';
import { SessionProvider, useSession, useSnapshot } from '../lib/session';
import { Loading, Txt, notice } from '../components/ui';
import { View } from 'react-native';
import { setReadOnly } from '../lib/api';
import { UpdateBanner } from '../components/UpdateBanner';
import { DialogHost } from '../components/Dialogs';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { startMonitoring } from '../lib/monitoring';
import { setHelpEmail } from '../lib/help';
import { useEffect } from 'react';

export default function RootLayout() {
  const [loaded] = useFonts({ Lora_500Medium, Caveat_500Medium, Inter_400Regular, Inter_500Medium, Inter_600SemiBold });
  if (!loaded) return <Loading />;
  return (
    <SessionProvider>
      <StatusBar style="dark" />
      <Guarded />
      <DialogHost />
      <UpdateBanner />
    </SessionProvider>
  );
}

/** Every screen behind the crash screen (Reference, Try again, Get help). */
function Guarded() {
  const { lang, viewingUntil } = useSession();
  useEffect(() => void startMonitoring(), []);
  // The operator's "view as" is read-only: a change shows a notice instead of being sent.
  useEffect(() => setReadOnly(viewingUntil ? () => notice(t(lang, 'readOnlyView')) : null), [viewingUntil, lang]);
  return (
    <ErrorBoundary lang={lang}>
      <Routes />
      {viewingUntil && <ViewingBanner until={viewingUntil} />}
    </ErrorBoundary>
  );
}

/** A band across the top during "view as", so it is never mistaken for Mike's own home. */
function ViewingBanner({ until }: { until: Date }) {
  const { lang } = useSession();
  const time = until.toLocaleTimeString(lang === 'en' ? 'en-US' : 'es-MX', { hour: 'numeric', minute: '2-digit' });
  return (
    <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0, paddingVertical: 6, paddingHorizontal: 12, backgroundColor: colors.danger }}>
      <Txt size={13} weight="semibold" color={colors.white} style={{ textAlign: 'center' }}>
        {t(lang, 'viewingAs', { time })}
      </Txt>
    </View>
  );
}

/** Signed out: only sign-in. Signed in without a house: only setup. Otherwise the app. */
function Routes() {
  const { ready, signedIn } = useSession();
  const snapshot = useSnapshot();
  // Get help carries the signed-in email, so Mike can find the account.
  if (snapshot.data?.owner.email) setHelpEmail(snapshot.data.owner.email);
  if (!ready || (signedIn && snapshot.isPending)) return <Loading />;
  const hasHouse = !!snapshot.data?.household;
  // An invited second owner arrives with a house but no name yet.
  const needsName = hasHouse && !snapshot.data?.owner.name;
  return (
    <Stack screenOptions={{ headerShown: false, contentStyle: { backgroundColor: colors.background } }}>
      <Stack.Protected guard={!signedIn}>
        <Stack.Screen name="sign-in" />
      </Stack.Protected>
      <Stack.Protected guard={signedIn && !hasHouse}>
        <Stack.Screen name="setup" />
      </Stack.Protected>
      <Stack.Protected guard={signedIn && needsName}>
        <Stack.Screen name="welcome-name" />
      </Stack.Protected>
      <Stack.Protected guard={signedIn && hasHouse && !needsName}>
        <Stack.Screen name="(tabs)" />
        <Stack.Screen name="room/[id]" />
        <Stack.Screen name="room-edit" options={{ presentation: 'modal' }} />
        <Stack.Screen name="task/[id]" />
        <Stack.Screen name="product-edit" options={{ presentation: "modal" }} />
        <Stack.Screen name="house-edit" />
        <Stack.Screen name="garden-setup" />
        <Stack.Screen name="checklist" />
        <Stack.Screen name="report-print" />
        <Stack.Screen name="report/[date]" />
        <Stack.Screen name="move-day" />
        <Stack.Screen name="devices" />
      </Stack.Protected>
      {/* Help is open to everyone, signed in or not. It stays last: when an address isn't available
          (signed out at "/"), the app opens the first screen in this list, which must be sign-in. */}
      <Stack.Screen name="help" />
    </Stack>
  );
}
